export function slugify(value: string) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

// Applied in `onChange` so a typed keystroke and a pasted string take the same path.
export function stripNonDigits(value: string): string {
    return value.replace(/\D/g, '');
}

// Display only — storage and validation stay on the raw 10-digit string.
// `5551234567` → `(555) 123-4567`. Returns the input unchanged if it isn't 10 digits, and `''` for
// a missing number so a form can seed a masked field straight from a nullable column.
export function formatPhone(phone: string | null): string {
    if (!phone) return '';
    const digits = stripNonDigits(phone);
    if (digits.length !== 10) return phone;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Whole dollars render without trailing zeros ($40), fractional amounts show 2 decimals ($30.01).
// Pass `alwaysShowCents` for line-item amounts that must always read as $35.00.
export function formatCurrencyFromCents(
    cents: number,
    currency: string = 'USD',
    options: { alwaysShowCents?: boolean } = {},
): string {
    const isWhole = cents % 100 === 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: options.alwaysShowCents || !isWhole ? 2 : 0,
        maximumFractionDigits: 2,
    }).format(cents / 100);
}

// A card is good until the last day of its expiration month, so a card expiring this month is
// still valid. An unknown expiration cannot be judged, so it is never flagged.
// The boundary is deliberately local rather than UTC: formatShortDate renders the billing date in
// the viewer's zone too, so the verdict always agrees with the date printed beside it.
export function isCardExpired(
    month: number | null,
    year: number | null,
    asOf: Date = new Date(),
): boolean {
    if (month === null || year === null) {
        return false;
    }
    const firstDayAfterExpiry = new Date(year, month, 1);
    return asOf >= firstDayAfterExpiry;
}

// Month is unpadded (4/29, not 04/29). Empty when the provider has never reported an
// expiration — callers drop the whole clause rather than substituting a placeholder.
export function formatCardExpiry(month: number | null, year: number | null): string {
    if (month === null || year === null) {
        return '';
    }
    return `${month}/${String(year % 100).padStart(2, '0')}`;
}

// Stripe reports `card.brand` as a lowercase id, and several are not brand names once capitalized
// (`jcb`, `unionpay`, `eftpos_au`, `cartes_bancaires`). These names match Stripe's own, so a card
// reads the same here as on its receipt. `unknown` is Stripe's placeholder for a brand it could
// not identify. https://docs.stripe.com/api/payment_methods/object#payment_method_object-card-brand
const CARD_BRAND_NAME: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
    eftpos_au: 'eftpos Australia',
    cartes_bancaires: 'Cartes Bancaires',
    link: 'Link',
    unknown: '',
};

// Empty when there is no name to show — callers drop the whole clause rather than printing an id.
export function formatCardBrand(brand: string | null): string {
    if (!brand) {
        return '';
    }
    const id = brand.toLowerCase();
    return CARD_BRAND_NAME[id] ?? id.charAt(0).toUpperCase() + id.slice(1);
}
